import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SelectBox } from "@/components/common/ui";
import { AlertTriangle } from "lucide-react";

const EMPTY = { name: "", company: "", email: "", phone: "", location: "", source: "", value: "", assigned_to: "", custom: {}, notes: "" };

function CustomField({ f, value, onChange }) {
  const tid = `lead-field-${f.key}`;
  if (f.type === "select") return <SelectBox value={value} onChange={onChange} testid={tid} className="w-full" allLabel="—" options={f.options.map((o) => ({ value: o, label: o }))} />;
  return <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
}

export default function LeadFormDialog({ open, onOpenChange, market: fixedMarket, lead, onSaved }) {
  const { meta, can, people } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [market, setMarket] = useState(fixedMarket || "dealer");
  const [dups, setDups] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDups(null);
    if (lead) {
      setF({ ...EMPTY, ...lead, value: lead.value || "", assigned_to: lead.assigned_to || "", custom: lead.custom || {} });
      setMarket(lead.market);
    } else {
      setF(EMPTY);
      setMarket(fixedMarket || "dealer");
    }
  }, [open, lead, fixedMarket]);

  const m = meta.markets.find((x) => x.key === market);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const setCustom = (k) => (v) => setF({ ...f, custom: { ...f.custom, [k]: v } });

  const save = async (force = false) => {
    setBusy(true);
    const body = { name: f.name, company: f.company, email: f.email, phone: f.phone, location: f.location, source: f.source,
      value: Number(f.value) || 0, custom: f.custom, force };
    if (can("leads.assign")) body.assigned_to = f.assigned_to || null;
    try {
      const { data } = lead ? await api.patch(`/leads/${lead.id}`, body) : await api.post("/leads", { ...body, market, notes: f.notes });
      toast.success(lead ? "Lead updated" : "Lead created");
      onOpenChange(false);
      onSaved?.(data);
    } catch (e) {
      if (e.response?.status === 409 && e.response.data?.detail?.duplicates) setDups(e.response.data.detail.duplicates);
      else toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lead-form-dialog">
        <DialogHeader><DialogTitle>{lead ? "Edit lead" : "New lead"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          {!lead && !fixedMarket && (
            <div className="space-y-1.5 sm:col-span-2"><Label>Market</Label>
              <SelectBox value={market} onChange={(v) => setMarket(v || "dealer")} testid="lead-market-select" className="w-full" options={meta.markets.map((x) => ({ value: x.key, label: x.name }))} />
            </div>
          )}
          <div className="space-y-1.5"><Label>Contact name *</Label><Input value={f.name} onChange={set("name")} data-testid="lead-name-input" /></div>
          <div className="space-y-1.5"><Label>Company / organization</Label><Input value={f.company} onChange={set("company")} data-testid="lead-company-input" /></div>
          <div className="space-y-1.5"><Label>Mobile number</Label><Input placeholder="0917 123 4567" value={f.phone} onChange={set("phone")} data-testid="lead-phone-input" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={f.email} onChange={set("email")} data-testid="lead-email-input" /></div>
          <div className="space-y-1.5"><Label>Location</Label><Input placeholder="City, Province" value={f.location} onChange={set("location")} data-testid="lead-location-input" /></div>
          <div className="space-y-1.5"><Label>Lead source</Label>
            <SelectBox value={f.source} onChange={set("source")} testid="lead-source-select" className="w-full" allLabel="—" options={(meta.settings.lead_sources || []).map((s) => ({ value: s, label: s }))} />
          </div>
          <div className="space-y-1.5"><Label>Estimated value (PHP)</Label><Input type="number" min="0" value={f.value} onChange={set("value")} data-testid="lead-value-input" /></div>
          {can("leads.assign") && (
            <div className="space-y-1.5"><Label>Assigned salesperson</Label>
              <SelectBox value={f.assigned_to} onChange={set("assigned_to")} testid="lead-assign-select" className="w-full" allLabel="Unassigned" options={people.filter((p) => p.assignable).map((p) => ({ value: p.id, label: p.name }))} />
            </div>
          )}
          {m?.fields.filter((x) => x.active).map((x) => (
            <div key={x.key} className="space-y-1.5"><Label>{x.label}</Label><CustomField f={x} value={f.custom[x.key]} onChange={setCustom(x.key)} /></div>
          ))}
          {!lead && <div className="space-y-1.5 sm:col-span-2"><Label>Initial notes</Label><Textarea value={f.notes} onChange={set("notes")} data-testid="lead-notes-input" /></div>}
        </div>
        {dups && (
          <div className="border border-amber-300 bg-amber-50 rounded p-3 text-sm" data-testid="duplicate-warning">
            <div className="flex items-center gap-2 font-semibold text-amber-800"><AlertTriangle className="h-4 w-4" /> Possible duplicate (same phone or email)</div>
            <ul className="mt-2 space-y-1 text-amber-900">
              {dups.map((d) => <li key={d.id}>• {d.name} {d.company ? `— ${d.company}` : ""} ({d.market}){d.archived ? " · archived" : ""}</li>)}
            </ul>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {dups ? (
            <Button onClick={() => save(true)} disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid="lead-save-anyway-button">Save anyway</Button>
          ) : (
            <Button onClick={() => save(false)} disabled={busy || !f.name.trim()} className="bg-orange-500 hover:bg-orange-600" data-testid="lead-save-button">{lead ? "Save changes" : "Create lead"}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
